import styled from 'styled-components'
import { ArrowRightIcon } from '@velasca/icons/arrow-right'
import { CheckIcon } from '@velasca/icons/check'
import { CloseIcon } from '@velasca/icons/close'
import { LoadingIcon } from '@velasca/icons/loading'

export const Container = styled.div<{ condensedLayout?: boolean }>`
  ${({ condensedLayout }) => condensedLayout && 'width: 100%;'};
  position: relative;
  display: flex;
  align-self: end;
  height: 4.4rem;
`

export const Button = styled.button<{
  success: boolean
  condensedLayout?: boolean
}>`
  display: flex;
  align-items: center;
  gap: ${({ condensedLayout }) => (condensedLayout ? '3rem' : '8rem')};
  ${({ condensedLayout }) =>
    condensedLayout && 'width: 100%; justify-content: space-between;'}
  font-size: 1.4rem;
  font-weight: 500;
  text-transform: uppercase;
  padding: 0 1.5rem;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  background-color: ${({ theme, success }) =>
    success ? theme.colors.primary : theme.colors.background};

  color: ${({ theme, success }) =>
    success ? theme.colors.text.secondary : theme.colors.text.primary};

  &:disabled {
    cursor: default;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary} !important;
    outline-offset: 2px !important;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary} !important;
    outline-offset: 2px !important;
  }

  &:focus:not(:focus-visible) {
    outline: none !important;
  }
`

export const IconCaret = styled(ArrowRightIcon)`
  width: 1rem;
  height: 1rem;
  transform: rotate(90deg);
`

export const IconEmpty = styled.div`
  width: 1rem;
  height: 1rem;
`

export const IconLoading = styled(LoadingIcon)`
  width: 1rem;
  height: 1rem;
  g {
    fill: ${({ theme }) => theme.colors.text.primary};
  }
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

export const IconSuccess = styled(CheckIcon)`
  width: 1rem;
  height: 1rem;
`

export const IconError = styled(CloseIcon)`
  width: 1rem;
  height: 1rem;
`

export const DropdownList = styled.ul`
  width: 100%;
  max-height: 157px;
  z-index: 1051;
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  overflow-y: auto;
  padding: 0;
  outline: none;
  min-width: var(--radix-dropdown-menu-trigger-width);
  margin-bottom: 0;

  scrollbar-color: ${({ theme }) => theme.colors.hover}
    ${({ theme }) => theme.colors.background};
  scrollbar-width: thin;

  /* Radix UI DropdownMenu.Content styles */
  &[data-state='open'] {
    transform-origin: var(--radix-dropdown-menu-content-transform-origin);
    animation: slideDownAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &[data-state='closed'] {
    transform-origin: var(--radix-dropdown-menu-content-transform-origin);
    animation: slideUpAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &[data-side='top'] {
    transform-origin: var(--radix-dropdown-menu-content-transform-origin);
    animation: slideDownAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  &[data-side='bottom'] {
    transform-origin: var(--radix-dropdown-menu-content-transform-origin);
    animation: slideUpAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes slideUpAndFade {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDownAndFade {
    from {
      opacity: 0;
      transform: translateY(-2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export const DropdownItem = styled.li<{ isOutOfStock?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
  padding: 1.2rem 1.5rem;

  a {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    justify-content: space-between;
    cursor: pointer;
    color: inherit;
  }

  span {
    font-size: 1.4rem;
    line-height: normal;
    font-weight: 500;
    text-transform: uppercase;
  }

  /* Radix UI DropdownMenu.Item states */
  &[data-highlighted] {
    background-color: ${({ theme }) => theme.colors.secondary};
    outline: none;
  }

  &[data-disabled] {
    color: ${({ theme }) => theme.colors.text.light};
    cursor: pointer;
  }

  ${({ isOutOfStock, theme }) =>
    isOutOfStock &&
    `
    color: ${theme.colors.text.light};
    cursor: pointer;

    &[data-highlighted] {
      background-color: transparent;
    }
  `}
`
